import { useMemo } from "react";
import { ROUNDS } from "@/data/rounds";
import { computeRounds, computeYTD, money } from "@/lib/scoring";
import { Crest } from "@/components/Crest";
import { StickyNav } from "@/components/StickyNav";
import { Leaderboard } from "@/components/Leaderboard";
import { RoundsSection } from "@/components/RoundsSection";
import { ClubhouseSection } from "@/components/ClubhouseSection";
import { RosterSection } from "@/components/RosterSection";
import { RulesSection } from "@/components/RulesSection";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { Reveal } from "@/components/Reveal";

const HeroStat = ({ label, value }) => (
  <div className="rounded-xl border border-cypress-gold/20 bg-cypress-green/30 px-3 py-3 sm:px-4">
    <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-cypress-cream/50 sm:text-[10px]">{label}</p>
    <p className="mt-1 break-words font-mono text-sm font-bold leading-snug text-cypress-gold sm:text-lg" title={value}>
      {value}
    </p>
  </div>
);

const Hero = ({ rounds, ytd }) => {
  const leader = ytd.players[0];
  return (
    <header className="mx-auto max-w-5xl px-4 pt-6 sm:px-6 sm:pt-8" data-testid="hero-bands">
      <div className="rounded-xl border border-cypress-gold/20 bg-cypress-green/30 p-3 sm:p-4">
        {/* Course photo. Capped at its native 725px width so it never upscales. */}
        <div className="relative mx-auto h-[35vh] min-h-[240px] w-full max-w-[725px] overflow-hidden rounded-lg sm:h-[45vh] sm:max-h-[366px] sm:min-h-[300px]">
          <img
            src="/images/green.jpg"
            alt="The green with the flagstick, willow trees behind"
            fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: "50% 55%" }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to top, rgba(12,31,20,0.6) 0%, rgba(12,31,20,0.22) 38%, rgba(12,31,20,0.03) 100%)",
            }}
          />
          <div className="relative flex h-full flex-col items-center justify-center px-6">
            <div className="relative">
              <div
                className="absolute left-1/2 top-1/2 h-[180%] w-[260%] -translate-x-1/2 -translate-y-1/2"
                style={{ background: "radial-gradient(ellipse, rgba(12,31,20,0.8) 0%, rgba(12,31,20,0) 70%)" }}
              />
              <Crest className="relative h-[110px] w-auto sm:h-[150px]" />
            </div>
            <p className="relative mt-3 text-xs font-bold uppercase tracking-[0.35em] text-cypress-gold [text-shadow:0_1px_10px_rgba(0,0,0,0.8)] sm:text-sm">
              Cypress Boys
            </p>
          </div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3" data-testid="hero-stat-strip">
        <HeroStat label="Rounds Played" value={String(rounds.length)} />
        <HeroStat label="Total Paid Out" value={money(ytd.totalPot)} />
        <HeroStat label="YTD Leader" value={leader ? `${leader.name} · ${money(leader.totalWon)}` : "—"} />
      </div>
    </header>
  );
};

export default function App() {
  const rounds = useMemo(() => computeRounds(ROUNDS), []);
  const ytd = useMemo(() => computeYTD(rounds), [rounds]);

  return (
    <div className="min-h-screen bg-cypress-night font-sans text-cypress-cream">
      <Hero rounds={rounds} ytd={ytd} />
      <StickyNav />
      <main>
        <section id="leaderboard" className="scroll-mt-12 sm:scroll-mt-14" aria-label="Leaderboard">
          <Reveal>
            <Leaderboard rounds={rounds} ytd={ytd} />
          </Reveal>
        </section>
        <AnnouncementBanner />
        <section id="rounds" className="scroll-mt-12 sm:scroll-mt-14" aria-label="Rounds">
          <Reveal>
            <RoundsSection rounds={rounds} />
          </Reveal>
        </section>
        <section id="clubhouse" className="scroll-mt-12 sm:scroll-mt-14" aria-label="The Clubhouse">
          <Reveal>
            <ClubhouseSection />
          </Reveal>
        </section>
        <section id="roster" className="scroll-mt-12 sm:scroll-mt-14" aria-label="The Cypress Boys">
          <Reveal>
            <RosterSection />
          </Reveal>
        </section>
        <section id="rules" className="scroll-mt-12 sm:scroll-mt-14" aria-label="Our Game">
          <Reveal>
            <RulesSection />
          </Reveal>
        </section>
      </main>
      <footer className="border-t border-cypress-gold/25 bg-cypress-night px-6 py-8 text-center" data-testid="site-footer">
        <p className="text-xs tracking-wide text-cypress-cream/50">
          Group Coordinator: <span className="text-cypress-cream/90">Nick Sivil</span>
        </p>
      </footer>
    </div>
  );
}
